/**
 * 检查项记录详情页
 *
 * 顶部面包屑：返回 + 标题 + 状态 Tag + 右侧状态操作按钮（按 status 动态展示）
 * 左右两列：
 *  - 左 1fr：检查项信息 / AI 分析详情 / 媒体资源
 *  - 右 360px：操作记录（sticky）
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Spin, message } from 'antd'
import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  LeftOutlined,
  PictureOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons'
import {
  CheckDetailResponse,
  CheckListItem,
  CheckRecordStatus,
  fetchCheckDetail,
  updateCheckStatus
} from '../api/check'
import { selectGroupAndUser } from '../api/client'
import {
  CHECK_ACTIONS_BY_STATUS,
  CHECK_STATUS_BG,
  CHECK_STATUS_COLOR,
  CHECK_STATUS_LABEL,
  CheckActionConfig
} from '../constants/check'
import StatusTag from '../shared/StatusTag'
import AiAnalysisDetail from './AiAnalysisDetail'
import MediaResources from './MediaResources'
import OperationLog from './OperationLog'
import StatusActionModal from './StatusActionModal'
import FixModal from './FixModal'
import ReviewModal from './ReviewModal'

interface DetailProps {
  // 雪花 ID 17-19 位,超 Number.MAX_SAFE_INTEGER;允许 string 以保留精度
  checkId: number | string
  dispatch: (...args: any[]) => any
  state: any
  onBack: () => void
  /** 状态变更成功后回调（用于通知父组件刷新列表/统计） */
  onChanged?: () => void
}

const SectionCard: React.FC<{
  icon: React.ReactNode
  title: string
  right?: React.ReactNode
  children: React.ReactNode
  style?: React.CSSProperties
}> = ({ icon, title, right, children, style }) => (
  <div className="myse-section" style={style}>
    <div className="myse-section__head">
      <div className="myse-section__title">
        {icon}
        <span>{title}</span>
      </div>
      {right && <div className="myse-section__right">{right}</div>}
    </div>
    {children}
  </div>
)

const InfoCell: React.FC<{
  label: string
  children: React.ReactNode
  accent?: boolean
}> = ({ label, children, accent }) => (
  <div className="myse-info-cell">
    <span className="myse-info-cell__label">{label}</span>
    <span
      className={
        'myse-info-cell__value' +
        (accent ? ' myse-info-cell__value--accent' : '')
      }
    >
      {children}
    </span>
  </div>
)

const Detail: React.FC<DetailProps> = ({
  checkId,
  dispatch,
  state,
  onBack,
  onChanged
}) => {
  // 权限：普通成员仅看到 提交整改 / 发起复议；管理员看全部
  //
  // 角色完全由后端按请求信封顶层 user_id 自动判定，前端不再做本地推断：
  //  - 响应 permission.role >= 1 → 管理员（预留 2/3 自动兼容）
  //  - 响应 permission.role === 0 或 接口未返回 → 按普通成员处理（最保守，避免误授权）
  //  详见 Web管理后台-检查项记录管理-权限收口接口变更说明.md
  const [data, setData] = useState<CheckDetailResponse | null>(null)
  const isAdmin = (data?.permission?.role ?? 0) >= 1
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [actionModal, setActionModal] = useState<{
    visible: boolean
    action: CheckActionConfig | null
  }>({ visible: false, action: null })
  const [fixOpen, setFixOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)

  const load = useCallback(async () => {
    if (!checkId) return
    setLoading(true)
    try {
      const detail = await fetchCheckDetail(dispatch, checkId)
      setData(detail)
    } catch (e) {
      message.error((e as Error)?.message || '加载详情失败')
    } finally {
      setLoading(false)
    }
  }, [dispatch, checkId])

  useEffect(() => {
    load()
  }, [load])

  // 操作 → 弹出对应模态框
  const openAction = useCallback((action: CheckActionConfig) => {
    if (action.action === 'submit_rectify') setFixOpen(true)
    else if (action.action === 'submit_appeal') setReviewOpen(true)
    else setActionModal({ visible: true, action })
  }, [])

  // 通用确认/驳回（审核通过/驳回整改/复议通过/驳回复议）
  const handleConfirmAction = useCallback(
    async (remark: string) => {
      const { action } = actionModal
      if (!action || !data) return
      const { group_id, user_id } = selectGroupAndUser(state)
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      setSubmitting(true)
      try {
        await updateCheckStatus(dispatch, {
          check_id: data.basic.check_id,
          action: action.action,
          remark,
          group_id,
          user_id
        })
        message.success('操作成功')
        setActionModal({ visible: false, action: null })
        load()
        onChanged && onChanged()
      } catch (e) {
        message.error((e as Error)?.message || '操作失败')
      } finally {
        setSubmitting(false)
      }
    },
    [actionModal, data, dispatch, state, load, onChanged]
  )

  // 提交整改
  const handleFixSubmit = useCallback(
    async (payload: {
      remark: string
      media_resources: import('../api/check').MediaResourceParam[]
    }) => {
      if (!data) return
      const { group_id, user_id } = selectGroupAndUser(state)
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      setSubmitting(true)
      try {
        await updateCheckStatus(dispatch, {
          check_id: data.basic.check_id,
          action: 'submit_rectify',
          remark: payload.remark,
          media_resources: payload.media_resources,
          group_id,
          user_id
        })
        message.success('已提交整改')
        setFixOpen(false)
        load()
        onChanged && onChanged()
      } catch (e) {
        message.error((e as Error)?.message || '提交失败')
      } finally {
        setSubmitting(false)
      }
    },
    [data, dispatch, state, load, onChanged]
  )

  // 发起复议
  const handleReviewSubmit = useCallback(
    async (payload: { reason: string; remark: string }) => {
      if (!data) return
      const { group_id, user_id } = selectGroupAndUser(state)
      if (!group_id || !user_id) {
        message.error('未获取到团队或用户信息')
        return
      }
      const combined = [`复议原因：${payload.reason}`, payload.remark]
        .filter(Boolean)
        .join('\n')
      setSubmitting(true)
      try {
        await updateCheckStatus(dispatch, {
          check_id: data.basic.check_id,
          action: 'submit_appeal',
          remark: combined,
          group_id,
          user_id
        })
        message.success('已发起复议')
        setReviewOpen(false)
        load()
        onChanged && onChanged()
      } catch (e) {
        message.error((e as Error)?.message || '提交失败')
      } finally {
        setSubmitting(false)
      }
    },
    [data, dispatch, state, load, onChanged]
  )

  if (loading || !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  const basic = data.basic
  const status = basic.status as CheckRecordStatus
  const statusColor = CHECK_STATUS_COLOR[status]
  const statusBg = CHECK_STATUS_BG[status]
  const statusLabel = basic.status_label || CHECK_STATUS_LABEL[status]

  const totalPhotos = (data.ai_analysis_list || []).reduce(
    (sum, r) =>
      sum + (r.media_list || []).filter((m) => m.media_type === 0).length,
    0
  )
  const totalVideos = (data.ai_analysis_list || []).reduce(
    (sum, r) =>
      sum + (r.media_list || []).filter((m) => m.media_type === 1).length,
    0
  )

  // 按当前状态计算可用操作（与列表行操作一致）
  // 整改/复议类(submit_rectify、submit_appeal) 仅普通成员；其余审核/驳回类仅管理员。
  // 两组互斥，按 role 分流（详见接口文档表四「前端按钮渲染规则」）。
  const allStatusActions = CHECK_ACTIONS_BY_STATUS[status] || []
  const isUserAction = (a: string) =>
    a === 'submit_rectify' || a === 'submit_appeal'
  const statusActions = allStatusActions.filter((a) =>
    isAdmin ? !isUserAction(a.action) : isUserAction(a.action)
  )

  return (
    <div className="check-detail">
      {/* 顶部面包屑：左 返回+标题+状态 / 右 状态操作按钮组 */}
      <div
        className="myse-breadcrumb"
        style={{ display: 'flex', justifyContent: 'space-between' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <a
            onClick={onBack}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: '#3B7DEB',
              fontSize: 13
            }}
          >
            <LeftOutlined /> 返回列表
          </a>
          <span style={{ width: 1, height: 14, background: '#E5E7EB' }} />
          <span style={{ fontSize: 13, color: '#1F2937', fontWeight: 500 }}>
            {basic.inspection}
          </span>
          <StatusTag label={statusLabel} color={statusColor} bg={statusBg} />
        </div>
        {statusActions.length > 0 && (
          <div style={{ display: 'flex', gap: 8 }}>
            {statusActions.map((cfg) => {
              // 颜色映射与列表行操作保持一致（空心 ghost 样式）：
              //   提交整改 → 橙 / 发起复议·审核通过·复议通过 → 绿 / driver=danger → 红 / 其他 → 蓝
              let cls = ''
              const isDanger = !!cfg.danger
              if (!isDanger && cfg.action === 'submit_rectify') {
                cls = 'myse-ghost-action myse-ghost-action--rectify'
              } else if (
                !isDanger &&
                (cfg.action === 'submit_appeal' ||
                  cfg.action === 'review_pass' ||
                  cfg.action === 'appeal_pass')
              ) {
                cls = 'myse-ghost-action myse-ghost-action--appeal'
              }
              return (
                <Button
                  key={cfg.action}
                  type="primary"
                  danger={isDanger}
                  ghost
                  className={cls || undefined}
                  onClick={() => openAction(cfg)}
                >
                  {cfg.label}
                </Button>
              )
            })}
          </div>
        )}
      </div>

      {/* 两列布局 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 360px',
          gap: 16,
          alignItems: 'start'
        }}
      >
        {/* 左列 */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0
          }}
        >
          {/* 检查项信息 */}
          <SectionCard icon={<EnvironmentOutlined />} title="检查项信息">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                rowGap: 18,
                columnGap: 24
              }}
            >
              <InfoCell label="基地名称">{basic.base_name || '-'}</InfoCell>
              <InfoCell label="项目名称">{basic.project_name || '-'}</InfoCell>
              <InfoCell label="整机编号" accent>
                {basic.project_number || '-'}
              </InfoCell>
              <InfoCell label="整机机型">
                <span style={{ fontFamily: 'Inter, system-ui' }}>
                  {basic.machine_model || '-'}
                </span>
              </InfoCell>
              <InfoCell label="装配工序">{basic.process_name || '-'}</InfoCell>
              <InfoCell label="检验序号">
                <span style={{ fontFamily: 'Inter, system-ui' }}>
                  {basic.inspection || '-'}
                </span>
              </InfoCell>
              <InfoCell label="检查项状态">
                <StatusTag
                  label={statusLabel}
                  color={statusColor}
                  bg={statusBg}
                />
              </InfoCell>
              <InfoCell label="图片" accent>
                {totalPhotos} 张
              </InfoCell>
              <InfoCell label="视频" accent>
                {totalVideos} 个
              </InfoCell>
            </div>
          </SectionCard>

          {/* AI 分析详情 */}
          <SectionCard icon={<SafetyCertificateOutlined />} title="AI 分析详情">
            <AiAnalysisDetail
              aiList={data.ai_analysis_list || []}
              knowledgeRules={data.knowledge_rules}
            />
          </SectionCard>

          {/* 媒体资源 */}
          <SectionCard
            icon={<PictureOutlined />}
            title="媒体资源"
            right={`${totalPhotos + totalVideos} 个文件 · ${
              data.ai_analysis_list?.length || 0
            } 次提交`}
          >
            <MediaResources rounds={data.ai_analysis_list || []} />
          </SectionCard>
        </div>

        {/* 右列 - sticky 操作记录 */}
        <SectionCard
          icon={<ClockCircleOutlined />}
          title="操作记录"
          style={{ position: 'sticky', top: 16 }}
          right={`${data.operation_logs?.length || 0} 条`}
        >
          <OperationLog logs={data.operation_logs || []} />
        </SectionCard>
      </div>

      {/* 通用确认/驳回（审核通过 / 驳回整改 / 复议通过 / 驳回复议） */}
      <StatusActionModal
        visible={actionModal.visible}
        action={actionModal.action}
        recordSummary={`${basic.inspection} · ${basic.project_number}`}
        submitting={submitting}
        onCancel={() => setActionModal({ visible: false, action: null })}
        onConfirm={handleConfirmAction}
      />

      {/* 提交整改：详情页已加载 ai_analysis_list，直接传入避免二次拉取 */}
      <FixModal
        visible={fixOpen}
        record={
          {
            check_id: basic.check_id,
            inspection: basic.inspection,
            project_number: basic.project_number
          } as CheckListItem
        }
        dispatch={dispatch}
        aiAnalysisList={data.ai_analysis_list || []}
        submitting={submitting}
        onCancel={() => setFixOpen(false)}
        onSubmit={handleFixSubmit}
      />

      {/* 发起复议 */}
      <ReviewModal
        visible={reviewOpen}
        record={
          {
            check_id: basic.check_id,
            inspection: basic.inspection,
            project_number: basic.project_number
          } as CheckListItem
        }
        submitting={submitting}
        onCancel={() => setReviewOpen(false)}
        onSubmit={handleReviewSubmit}
      />
    </div>
  )
}

export default Detail
